import React from 'react';
import { createSelector } from 'reselect';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import LinearProgress from '@material-ui/core/LinearProgress';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { IRootState } from '../../../../../@types/redux';
import { IGeoServerBlock } from '../../../../../types/model/masterData/geoserver/block';
import MasterDataGeoServerFunctions from '../../../../../store/masterData/geoserver/functions';
import { LngLatBoundsLike } from 'react-map-gl';
import GeoHelper from '../../../../../services/helper/geoHelper';
import { IGeoServerPoint, pointType } from '../../../../../types/model/masterData/geoserver/point';
import { CROP } from '../../../../../appConstants';

interface IGeoServerFieldAutocompleteProps {
    id ?: string;
    value ?: string | null;

    onChange : (value ?: string, bounds ?: LngLatBoundsLike | null, event ?: React.ChangeEvent<unknown>) => void;

    blocks : Array<IGeoServerBlock>;
    points : Array<IGeoServerPoint>;

    required ?: boolean;
    autoFocus ?: boolean;

    fullWidth ?: boolean;

    isLoading : boolean;
    disabled ?: boolean;
    name ?: string;

    label ?: string;

    divisionGuid ?: string | null;
    subDivisionGuid ?: string | null;
    departmentGuid ?: string | null;
    crop ?: CROP | null;
    type : pointType | null;
}

interface IGeoServerFieldAutocompleteState {
}

class GeoServerFieldAutocompleteComponent extends React.Component<IGeoServerFieldAutocompleteProps, IGeoServerFieldAutocompleteState> {
    constructor(props : IGeoServerFieldAutocompleteProps) {
        super(props);

        this.state = {
        };
    }

    public readonly componentDidMount = () => {
        this.loadData();
    };

    public readonly loadData = async () => {
        MasterDataGeoServerFunctions.getBlockList();
        MasterDataGeoServerFunctions.getPointList();
    };

    private readonly getData = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.blocks;
    private readonly getPoints = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.points;
    private readonly getValue = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.value;
    private readonly getRequired = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.required;
    private readonly getDivisionGuid = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.divisionGuid;
    private readonly getSubDivisionGuid = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.subDivisionGuid;
    private readonly getDepartmentGuidGuid = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.departmentGuid;
    private readonly getCrop = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.crop;
    private readonly getType = (state : IGeoServerFieldAutocompleteState, props : IGeoServerFieldAutocompleteProps) => props.type;
    
    private readonly getBlockAutocomplete = createSelector([
        this.getData,
        this.getRequired,
        this.getDivisionGuid,
        this.getSubDivisionGuid,
        this.getDepartmentGuidGuid,
        this.getPoints,
        this.getCrop,
        this.getType,
    ], (
        blocks,
        required,
        divisionGuid,
        subDivisionGuid,
        departmentGuid,
        points,
        crop,
        type,
    ) => {
        const blockGuids = lodash.chain(points)
            .filter(n => !type || n.type === type)
            .filter(n => !crop || n.crop === crop)
            .map(n => n.blockGuid)
            .uniq()
            .value();

        const blocksDrop = lodash.chain(blocks)
            .filter(n => (!crop && !type) || blockGuids.includes(n.guid))
            .filter(x => !divisionGuid || x.divisionGuid === divisionGuid)
            .filter(x => !subDivisionGuid || x.subDivisionGuid === subDivisionGuid)
            .filter(x => !departmentGuid || x.departmentGuid === departmentGuid)
            .uniqBy(n => n.fieldGuid)
            .map(n => ({
                label: n.fieldName,
                value: n.fieldGuid,
            }))
            .sortBy(n => n.label)
            .value();

        if (!required) {
            blocksDrop.unshift({
                label: 'ALL',
                value: '',
            });
        }

        return blocksDrop;
    });

    private readonly getSelectedValue = createSelector([
        this.getValue,
        this.getRequired,
        this.getBlockAutocomplete,
    ], (
        value,
        required,
        blocks,
    ) => {
        if (value && typeof(value) === 'string') {
            const block = blocks
                .find(x => x.value === value);

            return block ?? null;
        }

        if (!value && !required) {
            return {
                label: 'ALL',
                value: '',
            };
        }

        return null;
    });

    private readonly onChange = (event : React.ChangeEvent<unknown>, value : {
        label : string;
        value : string;
    } | null) => {
        const bounds = !value?.value ? null : GeoHelper.getPolygonBounds(
            this.props.blocks
                .filter(x => !this.props.divisionGuid || x.divisionGuid === this.props.divisionGuid)
                .filter(x => !this.props.subDivisionGuid || x.subDivisionGuid === this.props.subDivisionGuid)
                .filter(x => !this.props.departmentGuid || x.departmentGuid === this.props.departmentGuid)
                .filter(x => x.fieldGuid === value.value)
        );

        this.props.onChange(value?.value, bounds, event);
    };

    public readonly render = () => {
        const {
            required,
            fullWidth,
            isLoading,
            disabled,
            label,
        } = this.props;

        const blocks = this.getBlockAutocomplete(this.state, this.props);

        const value = this.getSelectedValue(this.state, this.props);
        return (
            <React.Fragment>
                <FormControl fullWidth={fullWidth} error={required && !value} required={required}>
                    <Autocomplete
                        disabled={isLoading || disabled}
                        id='block_select'
                        options={blocks}
                        value={value}
                        getOptionSelected={(option, val) => option.value === val.value}
                        getOptionLabel={option => option.label}
                        onChange={this.onChange}
                        disableClearable={required}
                        openOnFocus
                        renderInput={params => (
                            <TextField
                                error={required && !value}
                                required={required}
                                {...params}
                                fullWidth={fullWidth}
                                label={label ?? 'Field'}
                            />)}
                    />
                    {
                        required && !value &&
                            <FormHelperText error>Required</FormHelperText>
                    }
                </FormControl>
                <div className='wfill' style={{
                    minHeight: 8,
                }}>
                    {
                        isLoading &&
                        <LinearProgress />
                    }
                </div>
            </React.Fragment>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        blocks: state.masterData.geoserver.blocks,
        points: state.masterData.geoserver.points,
        isLoading: state.masterData.geoserver.isLoadingBlocks || state.masterData.geoserver.isLoadingPoints,
    };
};

const GeoServerFieldAutocomplete = connect(
    mapStateToProps,
)(GeoServerFieldAutocompleteComponent);

/**
 * Value should be guid
 */
export default GeoServerFieldAutocomplete;
